import * as React from 'react';

import { cn } from '@mint-lib/tw-utils';
import { type VariantProps } from 'class-variance-authority';
import { ToggleGroup as ToggleGroupPrimitive } from 'radix-ui';

import { toggleVariants } from './variants.jsx';

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants>
>({
  size: 'medium',
});

function ToggleGroup({
  className,
  size,
  children,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> &
  VariantProps<typeof toggleVariants>) {
  const providerValue = React.useMemo(() => {
    return {
      size: size || 'medium',
    };
  }, [size]);
  return (
    <ToggleGroupPrimitive.Root
      data-slot="toggle-group"
      data-size={size}
      className={cn(
        'group/toggle-group flex w-fit items-center rounded-sm',
        className,
      )}
      {...props}
    >
      <ToggleGroupContext.Provider value={providerValue}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
}

function ToggleGroupItem({
  className,
  children,
  size,
  icon,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> &
  VariantProps<typeof toggleVariants>) {
  const context = React.useContext(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      data-size={context.size || size}
      className={cn(
        toggleVariants({
          size: context.size || size,
          icon: context.icon || icon,
        }),
        'min-w-0 cursor-pointer rounded-none shadow-none first:rounded-l-sm last:rounded-r-sm focus:z-focus focus-visible:z-focus disabled:cursor-auto',
        className,
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
}

export { ToggleGroup, ToggleGroupItem };
